import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Box, CircularProgress, Grid, Stack, Typography, Card, CardContent, Divider,
  Autocomplete, TextField,
} from '@mui/material';
import { useDashboardData } from '../../context/DashboardDataContext';
import { DataAndFilterContext } from '../../context/DataAndFilterContext';
import ScoreBadge from '../tiles/ScoreBadge';
import ReceiptsList from '../tiles/ReceiptsList';
import WorkflowStepTile from '../tiles/WorkflowStepTile';

const lc = (s) => (s || '').toString().trim().toLowerCase();
const norm = (s) => lc(s).replace(/\s+/g, ' ').replace(/[^\w\s]/g, '');
const productKey = (s) => norm(s);

// Pull list of Job categories primarily from dashboard Job Maps; fallback to interviews
function listJobs(ctx, filteredData) {
  const fromMaps = (() => {
    const out = new Set();
    if (ctx?.jobMaps && typeof ctx.jobMaps === 'object') {
      for (const [k, m] of Object.entries(ctx.jobMaps)) {
        out.add(m?.job_to_be_done_category || m?.job_to_be_done || m?.title || m?.name || k);
      }
    } else if (typeof ctx?.getJobMapFiles === 'function') {
      const files = ctx.getJobMapFiles() || {};
      for (const [k, m] of Object.entries(files)) {
        out.add(m?.job_to_be_done_category || m?.job_to_be_done || m?.title || m?.name || k);
      }
    }
    return Array.from(out).filter(Boolean);
  })();

  if (fromMaps.length) return fromMaps.sort();

  // Fallback: derive from interviews
  const out = new Set();
  for (const f of filteredData || []) {
    for (const j of f.jobs_to_be_done || []) {
      out.add(j.job_to_be_done_category || j.job_to_be_done || j.raw_job_to_be_done);
    }
  }
  return Array.from(out).filter(Boolean).sort();
}

// Pull product options from interviews (solution_choice_category counts)
function listProducts(filteredData) {
  const counts = new Map();
  for (const f of filteredData || []) {
    const name = f.solution_choice_category || f.solution_choice || '';
    if (!name) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }));
}

// Extract columns from getJobSections response (the same shape your Dashboard tiles use)
function extractColumnsFromSections(sections) {
  if (!Array.isArray(sections)) return [];
  const sec =
    sections.find((s) => s.type === 'jobMapBoard') ||
    sections.find((s) => lc(s.title) === 'the job map');
  return sec?.columns || [];
}

export default function JobMapAgnostic() {
  const dash = useDashboardData();
  const { filteredData } = useContext(DataAndFilterContext);

  // ---- Job selector (dropdown) ----
  const jobList = useMemo(() => listJobs(dash, filteredData), [dash, filteredData]);
  const [selectedJob, setSelectedJob] = useState('');
  useEffect(() => {
    if (!selectedJob && jobList.length) setSelectedJob(jobList[0]);
  }, [jobList, selectedJob]);

  // ---- Product selector (dropdown) ----
  const productOptions = useMemo(() => listProducts(filteredData), [filteredData]);
  const productDropdownOptions = useMemo(
    () => [{ name: 'None', count: null }, ...productOptions],
    [productOptions]
  );
  const [selectedProduct, setSelectedProduct] = useState('None'); // store just the name
  // Keep the currently selected product if it still exists; else fall back to "None"
  useEffect(() => {
    if (
      selectedProduct !== 'None' &&
      !productOptions.find((p) => p.name === selectedProduct)
    ) {
      setSelectedProduct('None');
    }
  }, [productOptions, selectedProduct]);

  // ---- Load Job Map columns for the selected job from DashboardDataContext
  const [columns, setColumns] = useState([]);
  useEffect(() => {
    if (!dash?.ready || !selectedJob || typeof dash.getJobSections !== 'function') {
      setColumns([]);
      return;
    }
    const { sections } = dash.getJobSections(selectedJob) || { sections: [] };
    setColumns(extractColumnsFromSections(sections));
  }, [dash, selectedJob]);

  const ORDER = ['define', 'locate', 'prepare', 'confirm', 'execute', 'monitor', 'modify', 'conclude'];
  const sortIndex = (stage) => {
    const name = String(stage || '').toLowerCase();
    const hit = ORDER.findIndex((o) => name.includes(o));
    return hit === -1 ? 999 : hit;
  };
  const sortedColumns = useMemo(
    () =>
      [...(columns || [])].sort(
        (a, b) => sortIndex(a.stage || a.name || a.title) - sortIndex(b.stage || b.name || b.title)
      ),
    [columns]
  );

  // Filter helper: for a given step, keep only workflows that match the selected product
  const filterProductWorkflows = (step, product) => {
    const want = productKey(product || '');
    if (!want || want === productKey('None')) return [];
    const arr = Array.isArray(step?.product_workflow_steps) ? step.product_workflow_steps : [];
    return arr.filter((wf) => productKey(wf?.product_name || wf?.product) === want);
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Job Map</Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Explore the universal job stages and steps. To see real product workflows tied to a product, pick one below.
      </Typography>

      {/* Job dropdown */}
      <Box sx={{ mb: 2, maxWidth: 520 }}>
        <Autocomplete
          options={jobList}
          value={selectedJob || null}
          onChange={(_, val) => setSelectedJob(val || '')}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} label="Product-agnostic job steps" size="small" />
          )}
        />
      </Box>

      {/* Product dropdown */}
      <Box sx={{ mb: 1.5, maxWidth: 520 }}>
        <Autocomplete
          options={productDropdownOptions}
          getOptionLabel={(o) => (o?.name ? (o.count != null ? `${o.name} (${o.count})` : o.name) : '')}
          value={productDropdownOptions.find((o) => o.name === selectedProduct) || productDropdownOptions[0]}
          onChange={(_, val) => setSelectedProduct(val?.name || 'None')}
          disableClearable
          renderInput={(params) => (
            <TextField {...params} label="Product-specific workflow steps" size="small" />
          )}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {!dash?.ready ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading job map…</Typography>
        </Stack>
      ) : selectedJob && sortedColumns.length ? (
        <Box
          sx={{
            overflowX: 'auto',
            overflowY: 'visible',
            pb: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'nowrap',
              gap: 2,
              px: 2,
              py: 2,
              minHeight: 180,
            }}
          >
            {sortedColumns.map((col, ci) => (
              <Box key={ci} sx={{ flex: '0 0 auto', minWidth: 360 }}>
                {/* Stage title */}
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                  {col.stage}
                </Typography>

                {/* Steps shown in a row */}
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 1 }}>
                  {(col.steps || []).map((step, si) => {
                    const productWfs = filterProductWorkflows(step, selectedProduct);
                    return (
                      <Box key={si} sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 320, pb: 1 }}>
                        <Card variant="outlined" sx={{ flex: '0 0 320px' }}>
                          <CardContent>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {step.name || '—'}
                            </Typography>

                            {step.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                <strong>How they do it:</strong> {step.description}
                              </Typography>
                            )}

                            <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                              {Number.isFinite(step.satisfaction_score) && (
                                <ScoreBadge label="Satisfaction" value={step.satisfaction_score} metricKey="satisfaction" />
                              )}
                            </Stack>

                            <ReceiptsList receipts={step.receipts || []} />
                          </CardContent>
                        </Card>

                        {/* Product-specific workflow tiles under the step when a product is selected */}
                        {selectedProduct !== 'None' && (
                          <Box sx={{ mt: 0.5 }}>
                            {productWfs.length ? (
                              <Grid container spacing={1.5}>
                                {productWfs.map((wf, wi) => (
                                  <Grid item xs={12} key={wi}>
                                    <WorkflowStepTile
                                      title={wf.name || wf.step_name || '—'}
                                      description={wf.description || ''}
                                      product={wf.product_name || wf.product || selectedProduct}
                                      satisfaction={Number(wf.satisfaction_score)}
                                      receipts={Array.isArray(wf.receipts) ? wf.receipts.slice(0, 2) : []}
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No product workflow steps for “{selectedProduct}” in this step.
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {selectedJob ? `No job map found for “${selectedJob}”.` : 'Select a job to view the map.'}
        </Typography>
      )}
    </Box>
  );
}
